import React from "react";
import { Badge, Box, Icon, Link, Text } from "~/components-shared";
import { CollapsableTypeEnum, TopicFootprintTypeEnum } from "~/types/enums";
import Markdown from "~/components/Markdown";
import Collapsable from "~/components/Collapsable";
import FootprintActions from "~/components/FootprintActions";
import moment from "moment";
import { helpers } from "~/lib/helpers";
import styled from "styled-components";
const Underline = styled.span`
  text-decoration: underline;
`;
const StyledBox = styled(Box)`
  padding-left: 1em;
  position: relative;

  &:before {
    position: absolute;
    content: "";
    left: 0;
    top: 0;
    width: 3px;
    height: 100%;
    background-color: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.colors.primary.main;
}};
    border-radius: 3px;
  }
`;
const TopicFootprintContentProductboard = _ref2 => {
  let {
    footprint,
    hideDetails = false,
    hideMessage = false
  } = _ref2;
  const {
    actions,
    type,
    data
  } = footprint;
  if ([TopicFootprintTypeEnum.NOTE].includes(type)) {
    const {
      noteTitle,
      noteContent,
      noteUrl,
      noteOwnerName,
      noteOwnerEmail,
      noteState,
      noteComments,
      noteTags,
      customFields
    } = data;
    const noteStates = {
      unprocessed: {
        text: "Unprocessed",
        color: "translucentDanger",
        icon: "errorOutline"
      },
      processed: {
        text: "Processed",
        color: "translucentSuccess",
        icon: "tick"
      },
      archived: {
        text: "Archived",
        color: "translucent",
        icon: "archive"
      }
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, !hideDetails && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold"
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " a Productboard", " ", /*#__PURE__*/React.createElement(Link, {
      href: noteUrl,
      underline: true
    }, "note"), " ", noteOwnerName || noteOwnerEmail ? `owned by ${noteOwnerName || noteOwnerEmail}` : "", !!noteTags?.length && /*#__PURE__*/React.createElement(React.Fragment, null, " ", "tagged with ", /*#__PURE__*/React.createElement(Underline, null, noteTags.join(", ")))), /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4.5,
      lineHeight: 6,
      display: "block",
      s: {
        my: 1
      }
    }, noteTitle, /*#__PURE__*/React.createElement(Badge, {
      display: "inline",
      color: noteStates[noteState].color,
      s: {
        py: 0.5,
        ml: 2,
        mt: -0.5
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: noteStates[noteState].icon,
      scale: 3,
      s: {
        mr: 1
      }
    }), noteStates[noteState].text)), noteContent && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        mb: 3
      },
      collapsedText: "View description",
      expandedText: "Hide description"
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: noteContent,
      fontSize: 4,
      lineHeight: 6,
      display: "block"
    }), !!customFields.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      weight: "semiBold",
      s: {
        my: 3
      },
      display: "block"
    }, "Fields"), /*#__PURE__*/React.createElement(Box, {
      gap: 2,
      direction: "row"
    }, customFields.map(customField => {
      const parsedCustomValue = helpers.parseProductboardCustomField(customField);
      return /*#__PURE__*/React.createElement(Text, null, customField.name, ": ", /*#__PURE__*/React.createElement(Text, {
        weight: "semiBold"
      }, parsedCustomValue));
    }))))), !hideMessage && !!noteComments.length && /*#__PURE__*/React.createElement(StyledBox, {
      gap: 2,
      direction: "column",
      s: {
        py: 0
      }
    }, noteComments.map(comment => /*#__PURE__*/React.createElement(Collapsable, null, /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 3,
      color: "textSecondary",
      display: "block"
    }, comment.email, " on ", moment(comment.createdAt).format("ddd, MMM D")), /*#__PURE__*/React.createElement(Markdown, {
      text: comment.content,
      fontSize: 4,
      lineHeight: 6,
      display: "inline-block"
    })))));
  }
  if ([TopicFootprintTypeEnum.FEATURE].includes(type)) {
    const {
      featureName,
      featureDescription,
      featureArchived,
      featureLastHealthUpdateStatus,
      featureLastHealthUpdateMessage,
      featureOwnerEmail,
      featureStatusName,
      featureTimeframeStartDate,
      featureTimeframeEndDate,
      featureTimeframeGranularity,
      featureUrl,
      customFields,
      productName,
      productUrl
    } = data;
    const showDates = featureTimeframeStartDate && featureTimeframeStartDate !== "none" && featureTimeframeEndDate && featureTimeframeEndDate !== "none";
    const dateFormat = featureTimeframeGranularity === "month" ? "MMMM" : featureTimeframeGranularity === "year" ? "YYYY" : "ddd, MMM D";
    const healthStatuses = {
      "on-track": {
        text: "On track",
        color: "translucentSuccess",
        icon: "tick"
      },
      "off-track": {
        text: "Off track",
        color: "translucentDanger",
        icon: "errorOutline"
      },
      "needs-attention": {
        text: "Needs Attention",
        color: "translucentWarning",
        icon: "warningOutline"
      }
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, !hideDetails && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold"
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " a Productboard", " ", /*#__PURE__*/React.createElement(Link, {
      href: featureUrl,
      underline: true
    }, "feature"), " ", "on", " ", /*#__PURE__*/React.createElement(Link, {
      href: productUrl,
      underline: true
    }, productName), " ", "owned by ", featureOwnerEmail), /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4.5,
      lineHeight: 6,
      display: "block",
      s: {
        my: 1
      }
    }, featureName, /*#__PURE__*/React.createElement(Badge, {
      color: "translucent",
      display: "inline",
      s: {
        py: 0.5,
        ml: 2,
        mt: -0.5
      }
    }, featureStatusName), featureArchived && /*#__PURE__*/React.createElement(Badge, {
      color: "translucent",
      display: "inline",
      s: {
        py: 0.5,
        ml: 2,
        mt: -0.5
      }
    }, "Archived"), showDates && /*#__PURE__*/React.createElement(Badge, {
      color: "translucentPrimary",
      display: "inline",
      s: {
        py: 0.5,
        ml: 2,
        mt: -0.5
      }
    }, featureTimeframeGranularity !== "month" && featureTimeframeGranularity !== "year" && `${moment(featureTimeframeStartDate).format(dateFormat)}– `, " ", moment(featureTimeframeEndDate).format(dateFormat))), featureDescription && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        mb: 3
      },
      collapsedText: "View description",
      expandedText: "Hide description"
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: featureDescription,
      fontSize: 4,
      lineHeight: 6,
      display: "block"
    }), !!customFields.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      weight: "semiBold",
      s: {
        my: 3
      },
      display: "block"
    }, "Fields"), /*#__PURE__*/React.createElement(Box, {
      gap: 2,
      direction: "row"
    }, customFields.map(customField => {
      const parsedCustomValue = helpers.parseProductboardCustomField(customField);
      return /*#__PURE__*/React.createElement(Text, null, customField.name, ": ", /*#__PURE__*/React.createElement(Text, {
        weight: "semiBold"
      }, parsedCustomValue));
    }))))), !hideMessage && featureLastHealthUpdateStatus && /*#__PURE__*/React.createElement(Collapsable, {
      s: {
        mb: 1
      }
    }, /*#__PURE__*/React.createElement(Badge, {
      color: healthStatuses[featureLastHealthUpdateStatus].color,
      s: {
        mr: 2
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      src: healthStatuses[featureLastHealthUpdateStatus].icon,
      scale: 3,
      s: {
        mr: 1
      }
    }), healthStatuses[featureLastHealthUpdateStatus].text), /*#__PURE__*/React.createElement(Markdown, {
      text: featureLastHealthUpdateMessage,
      fontSize: 4,
      lineHeight: 6,
      display: "inline-block"
    })));
  }
  if ([TopicFootprintTypeEnum.COMPONENT].includes(type)) {
    const {
      componentName,
      componentDescription,
      componentOwnerEmail,
      componentUrl,
      productName,
      productUrl,
      customFields
    } = data;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold"
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " a Productboard", " ", /*#__PURE__*/React.createElement(Link, {
      href: componentUrl,
      underline: true
    }, "component"), " ", "on", " ", /*#__PURE__*/React.createElement(Link, {
      href: productUrl,
      underline: true
    }, productName), " ", "owned by ", componentOwnerEmail), /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4.5,
      lineHeight: 6,
      display: "block",
      s: {
        my: 1
      }
    }, componentName), componentDescription && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        mb: 3
      },
      collapsedText: "View description",
      expandedText: "Hide description"
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: componentDescription,
      fontSize: 4,
      lineHeight: 6,
      display: "block"
    }), !!customFields.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      weight: "semiBold",
      s: {
        my: 3
      },
      display: "block"
    }, "Fields"), /*#__PURE__*/React.createElement(Box, {
      gap: 2,
      direction: "row"
    }, customFields.map(customField => {
      const parsedCustomValue = helpers.parseProductboardCustomField(customField);
      return /*#__PURE__*/React.createElement(Text, null, customField.name, ": ", /*#__PURE__*/React.createElement(Text, {
        weight: "semiBold"
      }, parsedCustomValue));
    })))));
  }
  if ([TopicFootprintTypeEnum.PRODUCT].includes(type)) {
    const {
      productName,
      productDescription,
      productUrl,
      productOwnerEmail,
      customFields
    } = data;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      color: "textSecondary",
      weight: "semiBold"
    }, /*#__PURE__*/React.createElement(FootprintActions, {
      actions: actions
    }), " a Productboard", " ", /*#__PURE__*/React.createElement(Link, {
      href: productUrl,
      underline: true
    }, "product"), " ", "owned by ", productOwnerEmail), /*#__PURE__*/React.createElement(Text, {
      weight: "semiBold",
      fontSize: 4.5,
      lineHeight: 6,
      display: "block",
      s: {
        my: 1
      }
    }, productName), productDescription && /*#__PURE__*/React.createElement(Collapsable, {
      maxHeight: 0,
      type: CollapsableTypeEnum.BUTTON,
      s: {
        mb: 3
      },
      collapsedText: "View description",
      expandedText: "Hide description"
    }, /*#__PURE__*/React.createElement(Markdown, {
      text: productDescription,
      fontSize: 4,
      lineHeight: 6,
      display: "block"
    }), !!customFields.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      fontSize: 4,
      weight: "semiBold",
      s: {
        my: 3
      },
      display: "block"
    }, "Fields"), /*#__PURE__*/React.createElement(Box, {
      gap: 2,
      direction: "row"
    }, customFields.map(customField => {
      const parsedCustomValue = helpers.parseProductboardCustomField(customField);
      return /*#__PURE__*/React.createElement(Text, null, customField.name, ": ", /*#__PURE__*/React.createElement(Text, {
        weight: "semiBold"
      }, parsedCustomValue));
    })))));
  }
  return /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    weight: "semiBold"
  }, "Unknown event");
};
export default TopicFootprintContentProductboard;